// Module Import
//---------------------------------
import React from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import TextHeader from '../TextHeader/TextHeader'
// Component Import
//---------------------------------
import LinkAtom from '../../atoms/links/links'
import TypographyAtom from '../../atoms/typography/typography'
import Image from '../../atoms/images/images'
import AboutUsGraphic from '../AboutUsGraphic/AboutUsGraphic'
import { brandDarkGrey } from '../../../assets/consts/color'
import EmbeddedYouTubeVideo from '../EmbeddedYouTubeVideo/EmbeddedYouTubeVideo'

// Style Tokens
//---------------------------------

// Styled Component Declarations
//---------------------------------
const LockupWrapper = styled.div`
  padding: 40px 0;

  @media only screen and (min-width: 48em) {
    padding: 80px 0;
  }

  &:last-of-type {
    padding-bottom: 0;
  }

  .hide-if-mobile {
    @media only screen and (max-width: 767px) {
      display: none;
    }
  }

  & .quote-sign {
    margin-bottom: 40px;
    text-align: right;
    color: ${brandDarkGrey};
  }

  .video-wrapper {
    margin-bottom: 40px;

    @media only screen and (min-width: 1024px) {
      margin-bottom: 0;
    }
  }
`

const ImageWrapper = styled.div`
  margin-bottom: 24px;
  position: relative;
  z-index: -1;
  margin: 0 0 40px 0;
  display: flex;

  @media only screen and (min-width: 48em) {
    margin-bottom: 0;
  }
`

const CopyWrapper = styled.div`
  margin-bottom: 48px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  & > div:first-child {
    margin-bottom: 40px;
  }

  h2 {
    margin: 0;
  }

  h3 {
    margin: 0;
  }

  p {
    margin-top: 0;

    &:last-of-type {
      margin: 0;
    }
  }
`

const VerticalLockupWrapper = styled.div`
  padding: 80px 0;
`

const VerticalImageWrapper = styled.div`
  position: relative;
  z-index: -1;
  margin: 0 0 40px 0;
  display: flex;

  @media only screen and (min-width: 48em) {
    max-height: 600px;
    width: 100%;
  }
`

const VerticalCopyWrapper = styled.div`
  margin-bottom: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 24px;

  h2 {
    margin: 0;
  }

  h3 {
    margin: 0;
  }

  p {
    margin-top: 0;

    &:last-of-type {
      margin: 0;
    }
  }
`

const VideoWrapper = styled.div`
  position: relative;
  overflow: hidden;
  margin-bottom: 24px;

  @media only screen and (min-width: 64em) {
    margin-bottom: 0;
  }
`

// Component Export
//---------------------------------
export default function Lockup({
  direction,
  imgSrc,
  imgAltTag,
  videoFile,
  youTubeLink,
  heading,
  subheader = '',
  body,
  linkText = '',
  linkDestination = '',
  id,
  fieldsNames = {},
  aboutUsGraphicImages = [],
  isAboutUs,
  sign,
}) {
  return (
    <>
      {direction === 'Forward' && (
        <LockupWrapper key={id} id={id}>
          <div className="row top-sm between-md middle-lg">
            <div className="col-xs-12 col-md-6 start-xs">
              <CopyWrapper>
                <TextHeader
                  title={heading}
                  subtitle={subheader}
                  titleField={fieldsNames.heading}
                  subtitleField={fieldsNames.subheading}
                />
                <TypographyAtom type="BlockText" copyBlock={body} weight="BlockText" />
              </CopyWrapper>
              {sign && (
                <TypographyAtom
                  className="quote-sign"
                  as="div"
                  type="H4"
                  weight="Bold"
                  copy={sign}
                />
              )}
              {linkDestination && linkText ? (
                <LinkAtom
                  linkType="Simple Button Link"
                  linkDestination={linkDestination}
                  linkText={linkText}
                />
              ) : null}
            </div>
            <div className="first-xs last-md col-xs-12 col-md-6">
              {imgSrc && !videoFile && !youTubeLink && (
                <ImageWrapper>
                  <Image
                    imgSrc={imgSrc}
                    altTag={imgAltTag}
                    hasFrame="Left Clipped"
                    aspectRatio="1/1"
                  />
                </ImageWrapper>
              )}
              {!imgSrc && videoFile && !youTubeLink && (
                <VideoWrapper>
                  <video width="100%" height="100%" controls>
                    <source src={videoFile} type="video/webm" />
                  </video>
                </VideoWrapper>
              )}
              {youTubeLink && !imgSrc && !videoFile && (
                <div className="video-wrapper">
                  <EmbeddedYouTubeVideo youTubeLink={youTubeLink} />
                </div>
              )}
            </div>
          </div>
        </LockupWrapper>
      )}
      {direction === 'Reverse' && (
        <LockupWrapper key={id} id={id}>
          <div className="row between-md middle-lg">
            <div className=" col-xs-12 col-md-6 start-xs">
              <CopyWrapper>
                <TextHeader
                  title={heading}
                  subtitle={subheader}
                  titleField={fieldsNames.heading}
                  subtitleField={fieldsNames.subheading}
                  iaAboutUs={isAboutUs}
                />
                <TypographyAtom type="BlockText" copyBlock={body} weight="BlockText" />
              </CopyWrapper>
              {sign && (
                <TypographyAtom
                  className="quote-sign"
                  as="div"
                  type="H4"
                  weight="Bold"
                  copy={sign}
                />
              )}
              {linkDestination && linkText ? (
                <LinkAtom
                  linkType="Simple Button Link"
                  linkDestination={linkDestination}
                  linkText={linkText}
                />
              ) : null}
            </div>
            <div
              className={`first-xs first-md col-xs-12 col-md-6 ${isAboutUs ? ' hide-if-mobile' : ''}`}
            >
              {isAboutUs && aboutUsGraphicImages.length > 0 ? (
                <AboutUsGraphic desktopConsultancyImages={aboutUsGraphicImages} />
              ) : (
                <>
                  {imgSrc && !videoFile && !youTubeLink && (
                    <ImageWrapper>
                      <Image
                        imgSrc={imgSrc}
                        altTag={imgAltTag}
                        hasFrame="Left Clipped"
                        aspectRatio="1/1"
                      />
                    </ImageWrapper>
                  )}
                  {!imgSrc && videoFile && !youTubeLink && (
                    <VideoWrapper>
                      <video width="100%" height="100%" controls>
                        <source src={videoFile} type="video/webm" />
                      </video>
                    </VideoWrapper>
                  )}
                  {youTubeLink && !imgSrc && !videoFile && (
                    <div className="video-wrapper">
                      <EmbeddedYouTubeVideo youTubeLink={youTubeLink} />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </LockupWrapper>
      )}
      {direction === 'Vertical' && (
        <VerticalLockupWrapper key={id} id={id}>
          {imgSrc && !videoFile && (
            <VerticalImageWrapper className="col-xs-12">
              <Image
                imgSrc={imgSrc}
                altTag={imgAltTag}
                hasFrame="Bottom Top Clip"
                aspectRatio="16/9"
              />
            </VerticalImageWrapper>
          )}
          {!imgSrc && videoFile && (
            <VideoWrapper>
              <video width="100%" height="100%" controls>
                <source src={videoFile} type="video/webm" />
              </video>
            </VideoWrapper>
          )}
          <div className="col-xs-12">
            <VerticalCopyWrapper>
              <TextHeader
                title={heading}
                subtitle={subheader}
                titleField={fieldsNames.heading}
                subtitleField={fieldsNames.subheading}
              />
              <TypographyAtom type="BlockText" copyBlock={body} weight="BlockText" />
            </VerticalCopyWrapper>
            {linkDestination && linkText ? (
              <LinkAtom
                linkType="Simple Button Link"
                linkDestination={linkDestination}
                linkText={linkText}
              />
            ) : null}
          </div>
        </VerticalLockupWrapper>
      )}
    </>
  )
}

Lockup.propTypes = {
  direction: PropTypes.string.isRequired,
  imgSrc: PropTypes.object.isRequired,
  imgAltTag: PropTypes.string.isRequired,
  heading: PropTypes.string.isRequired,
  subheader: PropTypes.string,
  body: PropTypes.shape({}).isRequired,
  linkText: PropTypes.string,
  linkDestination: PropTypes.string,
}
